import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useDemoContext } from '../../contexts/DemoContext';
import {
  LayoutDashboard,
  Users,
  FileCheck,
  Bell
} from 'lucide-react';

const navigation = [
  { name: 'Dashboard', href: '/demo', icon: LayoutDashboard },
  { name: 'Vendors', href: '/demo/vendors', icon: Users },
  { name: 'Policies', href: '/demo/policies', icon: FileCheck },
];

export default function DemoLayout() {
  const location = useLocation();
  const { stats } = useDemoContext();

  const isActiveLink = (href) => {
    if (href === '/demo') {
      return location.pathname === '/demo' || location.pathname === '/demo/dashboard';
    }
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Demo Banner */}
      <div className="bg-slate-900 text-white px-4 py-3">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="font-medium">
            Interactive demo. Build a vendor list, add policies, and watch alerts update instantly. Changes reset on refresh.
          </p>
          <div className="flex items-center gap-3">
            <Link
              to="/signup"
              className="inline-flex items-center rounded-md bg-white px-4 py-2 font-semibold text-slate-900 hover:bg-slate-100 transition-colors"
            >
              Sign Up for Free
            </Link>
            <Link
              to="/login"
              className="hidden sm:inline text-slate-200 hover:text-white transition-colors"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-[1600px] lg:items-start">
        {/* Sidebar */}
        <aside className="hidden lg:block lg:w-64 lg:shrink-0">
          <div className="sticky top-0 h-screen border-r border-gray-200 bg-white">
            {/* Logo */}
            <div className="flex items-center h-16 px-6 border-b border-gray-200">
              <Link to="/demo" className="flex items-center">
                <img
                  src={`${process.env.PUBLIC_URL}/branding/logo_full_web.png`}
                  alt="PolicyNotify"
                  className="h-12 w-auto"
                />
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
              {navigation.map((item) => {
                const isActive = isActiveLink(item.href);
                const Icon = item.icon;

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className={`h-5 w-5 mr-3 ${isActive ? 'text-primary-600' : 'text-gray-400'}`} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            {/* Demo CTA */}
            <div className="p-4 border-t border-gray-200">
              <div className="bg-slate-100 rounded-xl p-4 mb-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-2">
                  Try the core workflow
                </h3>
                <p className="text-xs text-slate-600 mb-3">
                  Add a vendor, create a policy that expires in 14 days, then open notifications.
                </p>
                <Link
                  to="/signup"
                  className="block w-full bg-primary-600 text-white text-center py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                >
                  Sign Up for Free
                </Link>
              </div>
              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Already have an account? <span className="font-medium">Sign in</span>
                </Link>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-30 border-b border-gray-200 bg-white lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <Link to="/demo" className="flex items-center">
              <img
                src={`${process.env.PUBLIC_URL}/branding/logo_full_web.png`}
                alt="PolicyNotify"
                className="h-10 w-auto"
              />
            </Link>
            <Link
              to="/demo/notifications"
              className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-slate-50 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <Bell className="h-5 w-5" />
              {stats.unread_notifications > 0 && (
                <span className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                  {stats.unread_notifications > 9 ? '9+' : stats.unread_notifications}
                </span>
              )}
            </Link>
          </div>
          <nav className="flex gap-2 overflow-x-auto px-4 pb-4">
            {navigation.map((item) => {
              const isActive = isActiveLink(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>
          </header>

          {/* Top header */}
          <header className="sticky top-0 z-20 hidden lg:block h-16 bg-white border-b border-gray-200">
            <div className="flex items-center justify-between h-full px-6">
              <div>
                <p className="text-sm font-semibold text-slate-900">Public Demo</p>
                <p className="text-sm text-slate-500">Temporary sample workspace</p>
              </div>
              {/* Notification Bell */}
              <div className="relative">
                <Link
                  to="/demo/notifications"
                  className="relative p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Bell className="h-6 w-6" />
                  {stats.unread_notifications > 0 && (
                    <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {stats.unread_notifications > 9 ? '9+' : stats.unread_notifications}
                    </span>
                  )}
                </Link>
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="p-4 sm:p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
